<template>
  <f7-page name="group-buy-create">
    <f7-navbar :title="$t('groupBuy.createCampaign')" back-link />
    <AppLayout>
      <template #main>
        <f7-card class="gbc-intro" :padding="false">
          <f7-card-content class="gbc-intro-content" :padding="false">
            <div class="gbc-intro-title">{{ $t("groupBuy.createHint") }}</div>
          </f7-card-content>
        </f7-card>

        <f7-list inset strong form media-list outline dividers>
          <f7-list-item :title="$t('groupBuy.product')"  smart-select
        :smart-select-params="{
          openIn: 'popup',
          closeOnSelect: true,
          swipeToClose: true,
          searchbar: true,
          searchbarPlaceholder: $t('common.search'),
        }">
              <select
                v-model="productId"
                :disabled="!!forcedProductId"
                @change="onSelectProduct"
              >
                <option value="">{{ $t("groupBuy.selectProduct") }}</option>
                <option
                  v-for="p in products"
                  :key="p.id"
                  :value="p.id"
                >
                  {{ productName(p) }} — {{ fmtCfa(productPrice(p)) }}
                </option>
              </select>
           
          </f7-list-item>
          <f7-list-item :title="$t('groupBuy.price')" :after="fmtCfa(form.startingPrice)">
            <f7-slider
              v-model="form.startingPrice"
              :min="sliderMin"
              :max="sliderMax"
              :step="100"
              :label="false"
              color="green"
            />
          </f7-list-item>
          <f7-list-input v-model:value="form.minimumParticipants" type="number" :label="$t('groupBuy.minParticipants')" />
          <f7-list-input v-model:value="form.maximumParticipants" type="number" :label="$t('groupBuy.maxParticipants')" />
          <f7-list-input v-model:value="form.endsAt" type="datetime-local" :label="$t('groupBuy.endsAt')" />
          <f7-list-item :title="$t('groupBuy.escrowRequired')">
            <template #after>
              <f7-toggle v-model:checked="form.escrowRequired" color="green" />
            </template>
          </f7-list-item>
          <f7-list-input v-model:value="form.description" type="textarea" resizable :label="$t('groupBuy.description')" />
        </f7-list>

        <f7-block-title>{{ $t("groupBuy.tierEditor") }}</f7-block-title>
        <f7-list inset strong>
          <f7-list-item v-for="(tier, index) in tiers" :key="index" :title="$t('groupBuy.tierMinBuyers', { n: tier.minimumParticipants })" :subtitle="$t('groupBuy.tierUnitPrice', { price: fmtCfa(tier.unitPrice) })">
            <template #after>
              <f7-button outline small class="gbc-tier-remove" @click="removeTier(index)">
                {{ $t("groupBuy.removeTier") }}
              </f7-button>
            </template>
          </f7-list-item>
        </f7-list>
        <f7-block>
          <f7-button outline raised small @click="addTier">
            + {{ $t("groupBuy.addTier") }}
          </f7-button>
        </f7-block>

        <f7-block>
          <f7-button fill raised large :disabled="!canSubmit" @click="submit">
            {{ $t("groupBuy.createAndGo") }}
          </f7-button>
        </f7-block>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { fmtCfa } from "../constants/listing-options.js";
import {
  useGroupBuyStore,
  suggestGroupBuyTiers,
} from "../js/pinia-stores/groupBuy.js";
import { useMarketplaceStore } from "../js/pinia-stores/marketplace.js";
import { useMarketplaceProductStore } from "../js/pinia-stores/marketplaceProduct.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import i18n from "../js/i18n.js";

const props = defineProps({
  groupId: { type: String, required: true },
  productId: { type: String, default: "" },
});

const { t } = i18n.global;
const groupBuyStore = useGroupBuyStore();
const marketplaceStore = useMarketplaceStore();
const marketplaceProductStore = useMarketplaceProductStore();
const groupsStore = useGroupsStore();

const products = ref([]);
const productId = ref(props.productId || "");
const forcedProductId = computed(() => props.productId);

const form = reactive({
  startingPrice: 0,
  minimumParticipants: 2,
  maximumParticipants: 20,
  endsAt: toLocalInput(Date.now() + 7 * 86400000),
  escrowRequired: false,
  description: "",
});
const tiers = ref([]);

const selected = computed(() =>
  products.value.find((p) => String(p.id) === String(productId.value)),
);
const sliderMin = computed(() => Math.max(0, Math.floor((form.startingPrice * 0.5) / 100) * 100));
const sliderMax = computed(() => Math.max(100, Math.ceil((form.startingPrice * 2) / 100) * 100));

const canSubmit = computed(
  () =>
    !!selected.value &&
    Number(form.startingPrice) > 0 &&
    Number(form.minimumParticipants) > 0 &&
    Number(form.maximumParticipants) >= Number(form.minimumParticipants) &&
    !!form.endsAt &&
    tiers.value.every((t) => Number(t.minimumParticipants) > 0 && Number(t.unitPrice) > 0),
);

function productName(p) {
  return p.title || p.name || p.id;
}
function productPrice(p) {
  return p.price ?? p.unitPrice ?? 0;
}
function onSelectProduct() {
  const p = selected.value;
  if (!p) {
    return;
  }
  const price = productPrice(p);
  form.startingPrice = price;
  tiers.value = suggestGroupBuyTiers(price);
  form.description = p.description || "";
}
function addTier() {
  tiers.value.push({
    minimumParticipants: (tiers.value.length ? Number(tiers.value[tiers.value.length - 1].minimumParticipants) : Number(form.minimumParticipants)) + 1,
    unitPrice: Number(form.startingPrice),
    sortOrder: tiers.value.length + 1,
  });
}
function removeTier(index) {
  tiers.value.splice(index, 1);
}
function toLocalInput(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId).catch(() => {}),
    marketplaceStore.fetchGroupMarketplace(props.groupId).catch(() => {}),
  ]);
  const shop = marketplaceStore.getGroupShop(props.groupId);
  const list = shop?.id ? await marketplaceProductStore.getByShop(shop.id).catch(() => []) : [];
  products.value = Array.isArray(list) ? list : [];
  if (selected.value) onSelectProduct();
  else if (products.value[0]) onSelectProduct();
});

async function submit() {
  if (!canSubmit.value) return;
  f7.preloader.show();
  try {
    const payload = {
      productId: productId.value,
      variantId: null,
      title: selected.value?.title || selected.value?.name || "",
      description: form.description.trim(),
      startingPrice: Number(form.startingPrice),
      currency: selected.value?.currency || "XOF",
      minimumParticipants: Number(form.minimumParticipants),
      maximumParticipants: Number(form.maximumParticipants),
      startsAt: new Date().toISOString(),
      endsAt: new Date(form.endsAt).toISOString(),
      status: "DRAFT",
      finalTierPriceForAll: null,
      escrowRequired: !!form.escrowRequired,
    };
    const campaign = await groupBuyStore.createCampaign(payload);
    const sortedTiers = [...tiers.value]
      .sort((a, b) => Number(a.minimumParticipants) - Number(b.minimumParticipants))
      .map((tier, i) => ({
        campaignId: campaign.id,
        minimumParticipants: Number(tier.minimumParticipants),
        unitPrice: Number(tier.unitPrice),
        sortOrder: i + 1,
      }));
    for (const tier of sortedTiers) {
      await groupBuyStore.addTier(tier).catch(() => {});
    }
    f7.preloader.hide();
    f7.toast.show({ text: t("groupBuy.campaignCreated"), closeTimeout: 2000 });
    f7.views.main.router.navigate(
      `/groups/${props.groupId}/group-buy/campaigns/${campaign.id}`,
    );
  } catch (error) {
    f7.preloader.hide();
    f7.dialog.alert(
      error?.response?.data?.message || t("groupBuy.errorGeneric"),
    );
  }
}
</script>

<style scoped>
.gbc-intro {
  margin: 16px;
  box-shadow: none;
  border-radius: 12px;
  background: color-mix(in srgb, var(--f7-theme-color) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 16%, transparent);
}
.gbc-intro-content {
  position: relative;
  padding: 16px;
}
.gbc-intro-title {
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.gbc-tier-remove {
  --f7-button-outline-border-color: #c0392b;
  --f7-button-text-color: #c0392b;
  font-size: 12px;
  font-weight: 600;
}
</style>